import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Check if token is expired
    const isTokenExpired = (token) => {
        try {
            const decoded = jwtDecode(token);
            return decoded.exp * 1000 < Date.now();
        } catch (error) {
            return true;
        }
    };

    // Get user info from token
    const getUserFromToken = (token) => {
        try {
            return jwtDecode(token);
        } catch (error) {
            return null;
        }
    };

    // Login function
    const login = (token, userData) => {
        localStorage.setItem('token', token);
        const user = userData || getUserFromToken(token);
        setUser(user);

        // Set default Authorization header for all requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    };

    // Logout function
    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
        navigate('/login');
    };

    // Check auth state on app start and token changes
    useEffect(() => {
        const initAuth = () => {
            const token = localStorage.getItem('token');

            if (token && !isTokenExpired(token)) {
                // Valid token exists
                const userData = getUserFromToken(token);
                setUser(userData);
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            } else if (token && isTokenExpired(token)) {
                // Token exists but is expired
                localStorage.removeItem('token');
                if (window.location.pathname !== '/login') {
                    navigate('/login');
                }
            }
            setLoading(false);
        };

        initAuth();
    }, [navigate]);

    // Set up axios interceptor for 401 responses
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    // Auth context value
    const value = {
        user,
        login,
        logout,
        isAuthenticated: !!user,
        isTokenExpired,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading ? children : <div>Loading...</div>}
        </AuthContext.Provider>
    );
};

export default AuthContext;